import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ChevronLeft, Users, School, QrCode, Copy, Check, Mail, 
  Smartphone, Download, Maximize2, X
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_URL = "https://soph-ia.org";

const ClassQRCodePage = ({ user }) => {
  const navigate = useNavigate();
  const { classId } = useParams();
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [emailsSent, setEmailsSent] = useState(false);

  useEffect(() => {
    fetchClassData();
  }, [classId]);

  const fetchClassData = async () => {
    try {
      // Get class info and students
      const studentsRes = await axios.get(`${API}/students/by-class/${encodeURIComponent(classId)}`);
      setStudents(studentsRes.data || []);
      
      // Get establishment info
      if (studentsRes.data?.length > 0) {
        const etabId = studentsRes.data[0].etablissement_id;
        const etabRes = await axios.get(`${API}/etablissements/${etabId}`);
        setClassData({
          className: classId,
          etablissement: etabRes.data?.name || "Établissement",
          etablissementId: etabId,
          city: etabRes.data?.city || ""
        });
      } else {
        setClassData({
          className: classId,
          etablissement: "Établissement",
          etablissementId: "",
          city: ""
        });
      }
    } catch (error) {
      console.error("Error fetching class data:", error);
      setClassData({
        className: classId || "Classe",
        etablissement: "Établissement",
        etablissementId: "",
        city: ""
      });
    } finally {
      setLoading(false);
    }
  };

  const joinUrl = `${APP_URL}/rejoindre/${classData?.etablissementId}/${encodeURIComponent(classId || "")}`;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const sendInvitationEmails = async () => {
    setSendingEmails(true);
    try {
      const studentsWithEmail = students.filter(s => s.email);
      if (studentsWithEmail.length === 0) {
        alert("Aucun élève n'a d'adresse email enregistrée.");
        return;
      }
      
      await axios.post(`${API}/classes/send-invitations`, {
        class_name: classId,
        students: studentsWithEmail.map(s => ({
          email: s.email,
          first_name: s.first_name,
          last_name: s.last_name
        })),
        join_url: joinUrl,
        etablissement_name: classData?.etablissement
      });
      
      setEmailsSent(true);
    } catch (error) {
      console.error("Error sending emails:", error);
      alert("Erreur lors de l'envoi des emails.");
    } finally {
      setSendingEmails(false);
    }
  };

  const downloadQR = () => {
    const svg = document.getElementById("class-qr-code");
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    
    img.onload = () => {
      canvas.width = 1024;
      canvas.height = 1024;
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, 1024, 1024);
      
      const link = document.createElement("a");
      link.download = `QR_${classId}_SophIA.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };
    
    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1A365D] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#64748B]">Chargement...</p>
        </div>
      </div>
    );
  }

  // Fullscreen QR Code for projection
  if (fullscreen) {
    return (
      <div 
        className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center p-8"
        onClick={() => setFullscreen(false)}
      >
        <button 
          className="absolute top-4 right-4 p-2 bg-gray-100 rounded-full hover:bg-gray-200"
          onClick={() => setFullscreen(false)}
        >
          <X className="w-6 h-6" />
        </button>
        
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-6xl font-bold text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {classData?.etablissement}
          </h1>
          <h2 className="text-3xl md:text-5xl text-[#D4AF37]" style={{ fontFamily: 'Playfair Display, serif' }}>
            {classData?.className}
          </h2>
        </div>
        
        <div className="bg-white p-8 rounded-3xl shadow-2xl border-4 border-[#1A365D]">
          <QRCodeSVG
            id="class-qr-code-fullscreen"
            value={joinUrl}
            size={Math.min(window.innerWidth * 0.6, window.innerHeight * 0.5)}
            level="H"
            includeMargin={true}
            fgColor="#1A365D"
          />
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-2xl md:text-3xl text-[#64748B]">
            Scannez ce QR code pour rejoindre la classe
          </p>
          <p className="text-xl text-[#D4AF37] mt-2">
            soph-ia.org
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="class-qr-page">
      {/* Header */}
      <nav className="navbar px-4 md:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
            className="text-[#64748B] hover:text-[#1A365D]"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-[#1A365D] flex items-center justify-center">
            <QrCode className="w-5 h-5 text-white" />
          </div>
          <span className="navbar-logo hidden sm:block">QR Code Classe</span>
        </div>
      </nav>

      <main className="px-4 md:px-8 py-6 max-w-4xl mx-auto">
        {/* Class Info */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <School className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-[#64748B]">{classData?.etablissement}</span>
            {classData?.city && <span className="text-[#64748B]">• {classData.city}</span>}
          </div>
          <h1 className="text-3xl md:text-4xl text-[#1A365D] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {classData?.className}
          </h1>
          <div className="flex items-center justify-center gap-2">
            <Users className="w-4 h-4 text-[#64748B]" />
            <span className="text-[#64748B]">{students.length} élève{students.length > 1 ? 's' : ''}</span>
          </div>
        </div>

        {/* QR Code Card */}
        <Card className="card-sjh mb-6">
          <CardContent className="p-8 text-center">
            <div className="bg-white p-6 rounded-2xl inline-block shadow-lg border-2 border-[#E2E8F0] mb-6">
              <QRCodeSVG
                id="class-qr-code"
                value={joinUrl}
                size={280}
                level="H"
                includeMargin={true}
                fgColor="#1A365D"
              />
            </div>
            
            <p className="text-[#64748B] mb-4">
              Les élèves peuvent scanner ce QR code pour rejoindre la classe
            </p>
            
            {/* URL display */}
            <div className="bg-[#F8F9FA] rounded-lg p-3 flex items-center justify-between gap-2 mb-6">
              <code className="text-sm text-[#1A365D] truncate flex-1">
                {joinUrl}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={copyToClipboard}
                className="flex-shrink-0"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-[#22C55E]" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>

            {/* Action buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Button
                onClick={() => setFullscreen(true)}
                className="bg-[#1A365D] hover:bg-[#132745]"
              >
                <Maximize2 className="w-4 h-4 mr-2" />
                Plein écran
              </Button>
              <Button
                onClick={downloadQR}
                variant="outline"
                className="border-[#1A365D] text-[#1A365D]"
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger
              </Button>
              <Button
                onClick={sendInvitationEmails}
                disabled={sendingEmails || emailsSent || students.filter(s => s.email).length === 0}
                variant="outline"
                className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
              >
                <Mail className="w-4 h-4 mr-2" />
                {emailsSent ? "Envoyé !" : sendingEmails ? "Envoi..." : "Inviter par email"}
              </Button>
            </div>
            
            {students.filter(s => s.email).length === 0 && (
              <p className="text-sm text-[#F59E0B] mt-3">
                Aucun email d'élève enregistré. Utilisez le QR code ou ajoutez les emails lors de l'import.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="card-sjh">
          <CardContent className="p-6">
            <h3 className="text-lg font-semibold text-[#1A365D] mb-4 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-[#D4AF37]" />
              Comment les élèves rejoignent la classe
            </h3>
            
            <ol className="space-y-4">
              <li className="flex gap-3">
                <Badge className="bg-[#1A365D] text-white h-6 w-6 flex items-center justify-center rounded-full flex-shrink-0">1</Badge>
                <div>
                  <p className="font-medium text-[#1A365D]">Scanner le QR code</p>
                  <p className="text-sm text-[#64748B]">Avec l'appareil photo du téléphone ou une app de scan</p>
                </div>
              </li>
              <li className="flex gap-3">
                <Badge className="bg-[#1A365D] text-white h-6 w-6 flex items-center justify-center rounded-full flex-shrink-0">2</Badge>
                <div>
                  <p className="font-medium text-[#1A365D]">Compléter l'inscription</p>
                  <p className="text-sm text-[#64748B]">Prénom, nom et création du mot de passe</p>
                </div>
              </li>
              <li className="flex gap-3">
                <Badge className="bg-[#1A365D] text-white h-6 w-6 flex items-center justify-center rounded-full flex-shrink-0">3</Badge>
                <div>
                  <p className="font-medium text-[#1A365D]">Installer l'application (optionnel)</p>
                  <p className="text-sm text-[#64748B]">
                    <strong>iPhone</strong> : Safari → Partager → "Sur l'écran d'accueil"<br/>
                    <strong>Android</strong> : Chrome → Menu (⋮) → "Installer l'application"
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <Badge className="bg-[#D4AF37] text-white h-6 w-6 flex items-center justify-center rounded-full flex-shrink-0">4</Badge>
                <div>
                  <p className="font-medium text-[#1A365D]">Suivre le tutoriel</p>
                  <p className="text-sm text-[#64748B]">À la première connexion, un tutoriel interactif guide l'élève</p>
                </div>
              </li>
            </ol>
          </CardContent>
        </Card>

        {/* Student list preview */}
        {students.length > 0 && (
          <Card className="card-sjh mt-6">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-[#1A365D] mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#3B82F6]" />
                Élèves inscrits ({students.length})
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {students.slice(0, 12).map((student) => (
                  <div key={student.id} className="flex items-center gap-2 p-2 bg-[#F8F9FA] rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-[#1A365D] flex items-center justify-center text-white text-sm">
                      {student.first_name?.[0]}{student.last_name?.[0]}
                    </div>
                    <span className="text-sm text-[#1A365D] truncate">
                      {student.first_name} {student.last_name?.[0]}.
                    </span>
                    {student.email && <Mail className="w-3 h-3 text-[#22C55E] flex-shrink-0" />}
                  </div>
                ))}
                {students.length > 12 && (
                  <div className="flex items-center justify-center p-2 bg-[#E2E8F0] rounded-lg">
                    <span className="text-sm text-[#64748B]">+{students.length - 12} autres</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ClassQRCodePage;
